import { useEffect, useRef } from 'react'
import { motion, useSpring } from 'framer-motion'
import { HugeiconsIconProps } from '@hugeicons/react'

import { ProgressStepper } from '@/components/ui/ProgressStepper'

const ANIMATION_DURATION = 0.3

interface Step {
  value: string
  label: string
  icon: HugeiconsIconProps['icon']
  Component: React.ReactNode
}

interface StepTransitionsProps {
  steps: Step[]
  currentStep: number
  className?: string
}

export function StepTransitions({
  steps,
  currentStep,
  className = ''
}: StepTransitionsProps) {
  const lastIdx = useRef<number | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const spring = useSpring(0, { duration: ANIMATION_DURATION })

  useEffect(() => {
    if (!scrollRef.current) return
    if (lastIdx.current === currentStep) return
    lastIdx.current = currentStep

    spring.set(scrollRef.current.clientWidth * currentStep)
  }, [currentStep, spring])

  useEffect(() => {
    const unsubscribe = spring.on('change', left => {
      scrollRef.current?.scrollTo({
        left,
        behavior: 'smooth'
      })
    })

    return () => {
      unsubscribe()
    }
  }, [spring])

  const stepWidth = `${100 * steps.length}%`

  return (
    <div className={`w-full flex flex-col gap-4 ${className}`}>
      <div className="w-full overflow-hidden relative" ref={scrollRef}>
        <div className="flex flex-row items-start" style={{ width: stepWidth }}>
          {steps.map((step, idx) => {
            const show = idx === currentStep

            return (
              <motion.div
                key={step.value}
                className="flex-1"
                initial={{ height: show ? 'auto' : 0 }}
                animate={{ height: show ? 'auto' : 0 }}
                transition={{ duration: ANIMATION_DURATION }}
                inert={!show}
              >
                {step.Component}
              </motion.div>
            )
          })}
        </div>
      </div>
      <ProgressStepper value={steps[currentStep]?.value} steps={steps} />
    </div>
  )
}
